import { array, bool, func, string, number, object } from "prop-types";
import { useRef, useState, useEffect, forwardRef } from "react";

import Checkbox from "../Checkbox/Checkbox.jsx";
import Sorter from "./Sorter.jsx";

import cx from "classnames";
import useMatchMedia from "../../common/useMatchMedia.js";

const propTypes = {
  onFacetClick: func,
  clearAllFacets: func,
  showFacetsLabel: string,
  clearAllLabel: string,
  totalResults: number,
  facets: array,
  activeFacets: object,
  showDownCaret: bool,
  countCheckedFacets: func,
  checked: number,
  handleFacetClick: func,
  updateCountDisplay: func,
  eventTypeFilterMessage: string,
  sorters: array,
  showSortByLabel: string,
  isSorter: bool,
};

const DropdownFacets = forwardRef(function DropdownFacets(
  props,
  eventTypesRef,
) {
  const {
    onFacetClick,
    clearAllFacets,
    showFacetsLabel,
    clearAllLabel,
    totalResults,
    facets,
    activeFacets,
    showDownCaret,
    updateCountDisplay,
    eventTypeFilterMessage,
    sorters,
    showSortByLabel,
    isSorter,
  } = props;

  const matchesLarge = useMatchMedia("(min-width: 1200px)");

  // Keep track of state of individual facet menu to control toggling.
  const [menuOpen, setMenuOpen] = useState(false);

  // Keep track of how many facets are checked off in the menu.
  const [count, setCount] = useState(0);

  // Keep track of special case: event types in Dynamic Events Listing component
  // const [eventTypeSection, setEventTypeSection] = useState();
  const testRef = useRef(null);
  const [testEvents, setTestEvents] = useState(testRef.current);

  // On initial load and when facets are updated, count up how many facets are checked off,
  // and display this on the menu button.
  // Note: Need this on initial load because selected facets will persist on page refresh.
  useEffect(() => {
    let checkedValuesCount = 0;
    let checkedChildGroupValuesCount = 0;

    // Iterate through top-level values
    facets[0].values?.forEach((value) => {
      value.selected && checkedValuesCount++;
    });

    // Iterate through child group values
    facets[0].childGroups?.forEach((group) => {
      group.values?.forEach((value) => {
        value.selected && checkedChildGroupValuesCount++;
      });
    });

    // Add both types of values together
    setCount(checkedValuesCount + checkedChildGroupValuesCount);
  }, [updateCountDisplay, facets]);

  const outsideClickRef = useRef(null);
  const closeOpenMenus = (event) => {
    if (
      outsideClickRef.current &&
      menuOpen &&
      !outsideClickRef.current.contains(event.target)
    ) {
      setMenuOpen(false);
    }
  };

  document.addEventListener("mousedown", closeOpenMenus);

  const renderChildGroup = (group, facetId) => {
    if (group.childGroups && group.childGroups.length > 0) {
      return (
        <div key={group.id}>
          {group.childGroups.map((subGroup, i) => (
            <div key={subGroup.id}>
              <div className="facet__group-label">{group.label}:</div>
              <div className="facet__sub-group">
                {renderChildGroup(subGroup)}
              </div>
            </div>
          ))}
        </div>
      );
    } else if (group.values && group.values.length > 0) {
      return (
        <div key={group.id} className="facet__sub-group">
          <div className="facet__group-label">{group.label}:</div>
          <ul className="facet__value-list">
            {group.values.map((value, i) => renderValue(value, facetId))}
          </ul>
        </div>
      );
    } else {
      return null;
    }
  };

  // Render each individual facet (checkbox or radio button)
  const renderValue = (value, facetId) => {
    // Handle special case for events date range, where options
    // need to be radio buttons instead.
    if (facetId === "eventDateRange") {
      return (
        <li className="facet__value" key={value.id}>
          <label className="facet__value--radio">
            <input
              type="radio"
              id={value.id}
              value={value.id}
              name="eventDateRange"
              onChange={() => {
                onFacetClick(facetId, value.id);
                value.selected
                  ? setCount(count > 0 ? count - 1 : 0)
                  : setCount(count + 1);
              }}
              checked={value.selected}
            />
            <span className="facet__custom-radio-btn"></span>

            <span>{value.name}</span>
          </label>
        </li>
      );
    } else {
      // All other facets will be checkboxes.
      return (
        <li className="facet__value" key={value.id}>
          <Checkbox
            onClick={() => {
              onFacetClick(facetId, value.id);
              value.selected
                ? setCount(count > 0 ? count - 1 : 0)
                : setCount(count + 1);
            }}
            checked={value.selected}
          >
            {value.name}
          </Checkbox>
        </li>
      );
    }
  };

  const renderCheckboxes = (facet) => {
    // First determine if there are any checkboxes to be displayed;
    // need to look through both child groups and values,
    // depending on what the dropdown has.
    // If theres no checkboxes to display, we will display a message in the menu instead.
    let hasCheckboxes = false;

    if (facet.childGroups?.length) {
      let noFacets = 0;
      const valuesLength = facet.childGroups.length;
      facet.childGroups.map((group, i) => {
        if (group.values.length === 0) {
          noFacets++;
        }
      });

      noFacets === valuesLength
        ? (hasCheckboxes = false)
        : (hasCheckboxes = true);
    }

    if (facet.values?.length) {
      hasCheckboxes = true;
    }

    let lowercaseLabel = facet.label.toLowerCase();
    let facetLabel = "Select " + lowercaseLabel;
    const noResultsMsg = `There are currently no filters for ${lowercaseLabel}. \nPlease try updating your applied filters.`;

    // Handle special case where we want to hide checkboxes and show a message
    // for event types on mobile when no academic depts. are selected
    if (
      !matchesLarge &&
      facet.id === "eventTypes" &&
      !activeFacets?.hasOwnProperty("academicDepartments")
    ) {
      return (
        <div className="facet__group" key={facet.id}>
          {!matchesLarge && <div className="facet__label">{facetLabel}</div>}
          {eventTypeFilterMessage}
        </div>
      );
    } else {
      let checkboxes = hasCheckboxes ? (
        <div className="facet__group" key={facet.id}>
          {!matchesLarge && <div className="facet__label">{facetLabel}</div>}
          {renderFacet(facet)}
        </div>
      ) : (
        <div className="facet__no-results">{noResultsMsg}</div>
      );

      return checkboxes;
    }
  };

  const renderFacet = (facet) => {
    if (facet.childGroups && facet.childGroups.length > 0) {
      return (
        <div className="facet__group-wrapper">
          {facet.childGroups.map((group, i) =>
            renderChildGroup(group, facet.id),
          )}
        </div>
      );
    } else if (facet.values && facet.values.length > 0) {
      return (
        <ul
          className={`facet__value-list facet__value-list-${facet.id}`}
          ref={(el) => {
            if (facet.id === "eventTypes") {
              setTestEvents(el);
            }
          }}
        >
          {facet.values.map((value, i) => renderValue(value, facet.id))}
        </ul>
      );
    } else {
      return null;
    }
  };

  const toggleBodyScroll = () => {
    document.body.classList.toggle("no-scroll");
  };

  return (
    <div
      ref={outsideClickRef}
      className={cx(`dropdown-facet__parent dropdown-facet__${facets[0]?.id}`, {
        "dropdown-facet--flyout-left": isSorter,
      })}
    >
      {facets && facets.length > 0 && (
        <>
          {/* Render the dropdown menu button(s) */}

          <button
            className={cx("dropdown-facets__dropdown-btn js-dropdown-btn", {
              "dropdown-facets__dropdown-btn--desktop-open":
                menuOpen && matchesLarge,
              "dropdown-facets__dropdown-btn--mobile-open":
                menuOpen && !matchesLarge,
            })}
            type="button"
            onClick={(event) => {
              setMenuOpen(!menuOpen);
              event.target.blur();
              !matchesLarge && toggleBodyScroll();
            }}
            ref={eventTypesRef}
          >
            <span className="dropdown-facets__label">
              {showFacetsLabel
                ? showFacetsLabel
                : showSortByLabel
                ? showSortByLabel
                : facets[0].label}{" "}
              {showDownCaret ? (
                <span className="dropdown-facets__menu-icon"></span>
              ) : null}
            </span>

            {
              matchesLarge && !isSorter && (
                // (!menuOpen ? (
                <span className="dropdown-facets__checked-count">
                  {"\n"} ({count}) {count === 1 ? "Filter" : "Filters"} Selected
                </span>
              )
              // ) : (
              //   <span className="dropdown-facets__checked-count">
              //     {/* Select {facets[0].label.toLowerCase()} */}
              //     Select filter(s)
              //   </span>
              // ))}
            }
          </button>

          {/* On mobile, add black bar section on top of full-screen takeover */}
          {!matchesLarge && menuOpen && (
            <div className="dropdown-facets__dropdown-list--mobile-pre"></div>
          )}
          {/* Apply a desktop or mobile class depending on monitor width */}
          <div
            className={cx("facets__menu", {
              "dropdown-facets__dropdown-list--desktop": matchesLarge,
              "dropdown-facets__dropdown-list--mobile": !matchesLarge,
              "dropdown-facets__dropdown-list--open": menuOpen,
            })}
          >
            {/* On mobile, render full-screen takeover facets menu */}
            {!matchesLarge && (
              <div className="dropdown-facets__facets-top">
                <div></div>
                <h1>Filters</h1>
                <button
                  type="button"
                  aria-label="Close filters menu"
                  className="dropdown-facets__facets-close"
                  onClick={(event) => {
                    setMenuOpen(!menuOpen);
                    event.target.blur();
                    toggleBodyScroll();
                  }}
                >
                  {closeIcon}
                </button>
              </div>
            )}

            {!isSorter && facets.map((facet, i) => renderCheckboxes(facet))}

            {(isSorter || sorters?.length > 0) && (
              <Sorter
                sorters={sorters}
                onFacetClick={onFacetClick}
                matchesLarge={matchesLarge}
                showSortByLabel={showSortByLabel}
              />
            )}

            {/* Create fixed bottom bar containing "See x Results" and "Reset" buttons */}
            {!matchesLarge && (
              <div className="dropdown-facets__results-reset">
                <button
                  type="button"
                  className="dropdown-facets__results-btn"
                  onClick={(event) => {
                    setMenuOpen(!menuOpen);
                    event.target.blur();
                    toggleBodyScroll();
                  }}
                >
                  See {totalResults ? totalResults : null}{" "}
                  {totalResults > 1 ? "Results" : "Result"}
                </button>

                <button
                  type="button"
                  className="dropdown-facets__reset-btn"
                  onClick={() => clearAllFacets()}
                >
                  {clearAllLabel}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
});

DropdownFacets.propTypes = propTypes;
export default DropdownFacets;

const closeIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="feather feather-x"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
